import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

export const Thread = ({ t }) => (
  <ul className="mb-3 space-y-2">{t.messages.map((m) => (
    <li key={`${m.at}-${m.author}`} className={`rounded-sm border px-3 py-2 text-sm ${m.staff ? "border-[#E5202E]/40 bg-[#E5202E]/5" : "border-[#1E2D4A]"}`}>
      <div className="mb-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">{m.author}{m.staff ? " · staff" : ""} · {fmtTime(m.at)}</div>
      <div className="whitespace-pre-wrap text-slate-200">{m.text}</div>
    </li>
  ))}</ul>
);

const Ticket = ({ t, cid, reload }) => {
  const [text, setText] = useState("");
  const reply = async (e) => { e.preventDefault(); try { await api.post(`/communities/${cid}/tickets/${t.id}/reply`, { text }); setText(""); reload(); } catch (err) { toast.error(errMsg(err)); } };
  const close = async () => { try { await api.post(`/communities/${cid}/tickets/${t.id}/close`); toast.success("Ticket closed"); reload(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Card testId={`ticket-${t.id}`} title={`#${t.number} · ${t.subject}`} right={<span className={`font-mono text-xs uppercase ${t.status === "open" ? "text-emerald-400" : "text-slate-500"}`}>{t.status}</span>}>
      <p className="mb-3 font-mono text-xs text-slate-500">{t.name} · {fmtTime(t.created_at)}{t.thread_id ? " · Discord thread linked" : ""}{t.discord_error ? ` · Discord: ${t.discord_error.slice(0, 80)}` : ""}</p>
      <Thread t={t} />
      {t.status === "open" && <form onSubmit={reply} className="flex gap-2">
        <Input data-testid={`ticket-reply-${t.id}`} required placeholder="Reply as staff" value={text} onChange={(e) => setText(e.target.value)} className="border-[#1E2D4A] bg-[#050811]" />
        <button data-testid={`ticket-send-${t.id}`} className={BTN_PRIMARY}><Send className="h-4 w-4" /></button>
        <button type="button" data-testid={`ticket-close-${t.id}`} onClick={close} className={BTN_SECONDARY}><X className="h-4 w-4" />Close</button>
      </form>}
    </Card>
  );
};

export default function Tickets() {
  const { active } = useAuth();
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/communities/${active.id}/tickets?status=${status}`).then((r) => setRows(r.data)).catch(() => {}), [active.id, status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader eyebrow="Support" title="Tickets" description="Opened with /ticket in Discord or from the player portal. Each ticket gets a private Discord thread when a tickets channel is set on Discord Tools."
        actions={["open", "closed", ""].map((s) => <button key={s || "all"} data-testid={`tickets-filter-${s || "all"}`} onClick={() => setStatus(s)} className={`rounded-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${status === s ? "bg-[#E5202E]/15 text-[#E5202E]" : "text-slate-400"}`}>{s || "all"}</button>)} />
      {rows.length === 0 ? <EmptyState icon={LifeBuoy} title="No tickets" testId="tickets-empty">Nothing here right now.</EmptyState> : <div className="space-y-4">{rows.map((t) => <Ticket key={t.id} t={t} cid={active.id} reload={load} />)}</div>}
    </div>
  );
}
